import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, UserPlus, Trash2 } from "lucide-react";

const ROLE_OPTIONS = [
  { value: "dispatcher", label: "Dispatcher" },
  { value: "driver", label: "Driver" },
  { value: "readonly", label: "Read-only staff" },
  { value: "admin", label: "Administrator" },
];

const ROLE_LABEL = {
  admin: "Administrator", dispatcher: "Dispatcher", driver: "Driver", readonly: "Read-only staff",
};

export function StaffTab({ staff, createStaff, updateStaff, deleteStaff, currentUserId }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "dispatcher" });
  const [creating, setCreating] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    const ok = await createStaff(form);
    setCreating(false);
    if (ok) setForm({ name: "", email: "", password: "", phone: "", role: "dispatcher" });
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Create form */}
      <div className="lg:col-span-1">
        <form onSubmit={submit} className="rounded-lg border border-white/10 bg-card p-6 space-y-4" data-testid="staff-create-form">
          <div className="flex items-center gap-2 mb-2">
            <UserPlus className="h-4 w-4 text-white" />
            <h3 className="text-white text-sm uppercase tracking-luxe">Invite staff</h3>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-luxe text-muted-foreground">Name</Label>
            <Input data-testid="staff-name" required value={form.name} onChange={(e) => set("name", e.target.value)}
              className="mt-2 h-11 bg-secondary border-white/10 text-white" placeholder="Jane Dispatcher" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-luxe text-muted-foreground">Email</Label>
            <Input data-testid="staff-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)}
              className="mt-2 h-11 bg-secondary border-white/10 text-white" placeholder="jane@company.com" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-luxe text-muted-foreground">Temp password</Label>
            <Input data-testid="staff-password" type="text" required minLength={8} value={form.password} onChange={(e) => set("password", e.target.value)}
              className="mt-2 h-11 bg-secondary border-white/10 text-white" placeholder="min 8 characters" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-luxe text-muted-foreground">Phone (optional)</Label>
            <Input data-testid="staff-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)}
              className="mt-2 h-11 bg-secondary border-white/10 text-white" placeholder="+1 (514) 555-0100" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-luxe text-muted-foreground">Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="staff-role" className="mt-2 h-11 bg-secondary border-white/10 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button data-testid="staff-create-btn" type="submit" disabled={creating}
            className="w-full rounded-full bg-white text-black hover:bg-white/85">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
          </Button>
        </form>
      </div>

      {/* Staff list */}
      <div className="lg:col-span-2 rounded-lg border border-white/10 bg-card overflow-hidden">
        <table className="w-full text-sm" data-testid="staff-table">
          <thead>
            <tr className="border-b border-white/10 text-left text-muted-foreground text-xs uppercase tracking-luxe">
              <th className="px-5 py-4">Name</th><th className="px-5 py-4">Email</th>
              <th className="px-5 py-4">Role</th><th className="px-5 py-4">Status</th><th className="px-5 py-4"></th>
            </tr>
          </thead>
          <tbody>
            {staff.map((s) => (
              <tr key={s.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`staff-row-${s.id}`}>
                <td className="px-5 py-4 text-white">{s.name}{s.id === currentUserId && <span className="text-muted-foreground text-xs ml-2">(you)</span>}</td>
                <td className="px-5 py-4 text-white/80">{s.email}</td>
                <td className="px-5 py-4">
                  {s.id === currentUserId ? (
                    <span className="text-white/80">{ROLE_LABEL[s.role]}</span>
                  ) : (
                    <Select value={s.role} onValueChange={(v) => updateStaff(s.id, { role: v })}>
                      <SelectTrigger data-testid={`staff-role-select-${s.id}`} className="h-9 w-40 bg-secondary border-white/10 text-white text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </td>
                <td className="px-5 py-4">
                  {s.id === currentUserId ? (
                    <span className="text-[10px] uppercase tracking-luxe px-2.5 py-1 rounded-full border text-white border-white/40 bg-white/10">Active</span>
                  ) : (
                    <button data-testid={`staff-toggle-${s.id}`} onClick={() => updateStaff(s.id, { active: s.active === false })}
                      className={`text-[10px] uppercase tracking-luxe px-2.5 py-1 rounded-full border ${s.active === false ? "text-muted-foreground border-white/15" : "text-white border-white/40 bg-white/10"}`}>
                      {s.active === false ? "Inactive" : "Active"}
                    </button>
                  )}
                </td>
                <td className="px-5 py-4 text-right">
                  {s.id !== currentUserId && (
                    <button data-testid={`staff-delete-${s.id}`} onClick={() => deleteStaff(s.id)}
                      className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="h-4 w-4" /></button>
                  )}
                </td>
              </tr>
            ))}
            {staff.length === 0 && <tr><td colSpan={5} className="px-5 py-16 text-center text-muted-foreground">No staff accounts yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function MyTripsTab({ reservations, backendUrl }) {
  if (reservations.length === 0) {
    return <div className="rounded-lg border border-white/10 bg-card p-16 text-center text-muted-foreground" data-testid="my-trips-empty">No trips assigned to you yet.</div>;
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="my-trips-grid">
      {reservations.map((r) => (
        <div key={r.id} className="rounded-lg border border-white/10 bg-card p-6" data-testid={`my-trip-${r.id}`}>
          <div className="flex items-center justify-between mb-3">
            <span className="font-mono text-xs text-white/70">{r.reservation_number || r.id}</span>
            <span className="text-[10px] uppercase tracking-luxe px-2.5 py-1 rounded-full border border-white/20 text-white/80 capitalize">{(r.status || "").replace(/_/g, " ")}</span>
          </div>
          <h3 className="text-white font-medium">{r.service_label}</h3>
          <p className="text-muted-foreground text-sm mt-1">{r.date} · {r.time}</p>
          <div className="mt-3 space-y-1 text-sm">
            <p className="text-white/80"><span className="text-muted-foreground">Pickup:</span> {r.pickup_location}</p>
            {r.dropoff_location && <p className="text-white/80"><span className="text-muted-foreground">Drop-off:</span> {r.dropoff_location}</p>}
            <p className="text-white/80"><span className="text-muted-foreground">Client:</span> {r.name} · {r.phone}</p>
          </div>
          <a href={`/admin/trip-sheet/${r.id}`} target="_blank" rel="noopener noreferrer"
            data-testid={`my-trip-sheet-${r.id}`}
            className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-luxe px-3 py-1.5 rounded-full border border-white/20 text-white hover:bg-white hover:text-black transition-colors">
            Trip sheet
          </a>
        </div>
      ))}
    </div>
  );
}
